/**
 * ChangeAppointmentDialog — re-links (or unlinks) the appointment a consultation
 * is attached to.
 *
 * Lists the patient's appointments in a select; appointments already tied to a
 * different consultation — or that never happened (cancelled / no-show) — are
 * disabled, and a "No appointment" option clears the link entirely. Saving
 * issues a `link-appointment` PATCH on the consultation.
 */
import { useState } from 'react'
import { toast } from 'sonner'

import {
  APPOINTMENT_TYPES_BY_ID,
  type AppointmentStatus,
  type AppointmentWithRelations,
} from '@snootsvet/shared'

import { describeInZones, useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import { usePatchConsultation } from '@/frontend/hooks/api/consultations/use-patch-consultation'
import { useListPatientAppointments } from '@/frontend/hooks/api/patients/use-list-patient-appointments'

/** Sentinel select value representing "unlink from any appointment". */
const NO_APPOINTMENT = 'none'

/** Statuses whose appointments never took place, so nothing can be consulted on them. */
const UNLINKABLE_STATUSES = new Set<AppointmentStatus>(['cancelled', 'no-show'])

interface ChangeAppointmentDialogProps {
  consultationId: string
  patientId: string
  /** The appointment the consultation is currently linked to, or null. */
  currentAppointmentId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ChangeAppointmentDialog({
  open,
  onOpenChange,
  ...props
}: ChangeAppointmentDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <ChangeAppointmentDialogContent {...props} onOpenChange={onOpenChange} />}
    </Dialog>
  )
}

/**
 * Inner content — mounts fresh each time the dialog opens, so the selection resets
 * to the current link and the appointments query only runs when it's actually needed.
 */
function ChangeAppointmentDialogContent({
  consultationId,
  patientId,
  currentAppointmentId,
  onOpenChange,
}: Omit<ChangeAppointmentDialogProps, 'open'>) {
  const viewerFmt = useViewerFormat()
  const { data: appointments, isLoading } = useListPatientAppointments(patientId)
  const { mutateAsync: patchConsultation, isPending } = usePatchConsultation()

  const [selected, setSelected] = useState(currentAppointmentId ?? NO_APPOINTMENT)

  /** Builds a human label for an appointment, e.g. "Vaccination · 24 Jul 2026, 14:30". */
  const appointmentLabel = (appt: AppointmentWithRelations) => {
    const typeName =
      appt.appointmentTypeName ??
      APPOINTMENT_TYPES_BY_ID[appt.appointmentTypeId]?.name ??
      appt.appointmentTypeId
    // Rendered at the appointment's clinic; a plain string can't hold the
    // annotation as a separate span, so the viewer's differing reading (if any)
    // is appended in parentheses instead.
    const { primary, annotation } = describeInZones(
      appt.startTime,
      appt.clinicId,
      viewerFmt,
      'datetime'
    )
    const when = annotation ? `${primary} (${annotation})` : primary
    // Call out the statuses that make an appointment unlinkable, so a disabled
    // row reads as deliberate rather than broken.
    const status = UNLINKABLE_STATUSES.has(appt.status) ? ` — ${appt.status}` : ''
    return `${typeName} · ${when}${status}`
  }

  // Most recent appointments first — the one being consulted on is usually near "now".
  const sorted = [...(appointments ?? [])].sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
  )

  // Base UI resolves the trigger label from this list, so it must cover every value.
  const items = [
    { label: 'No appointment', value: NO_APPOINTMENT },
    ...sorted.map((appt) => ({ label: appointmentLabel(appt), value: appt.id })),
  ]

  /** True when an appointment is already linked to a *different* consultation. */
  const isTakenByOther = (appt: AppointmentWithRelations) =>
    appt.consultation != null && appt.consultation.id !== consultationId

  /**
   * The appointment currently linked stays selectable whatever its state — otherwise
   * an appointment cancelled after the fact would make the existing link unrepresentable.
   */
  const isDisabled = (appt: AppointmentWithRelations) =>
    appt.id !== currentAppointmentId &&
    (isTakenByOther(appt) || UNLINKABLE_STATUSES.has(appt.status))

  async function handleSave() {
    const appointmentId = selected === NO_APPOINTMENT ? null : selected
    if (appointmentId === currentAppointmentId) {
      onOpenChange(false)
      return
    }
    try {
      await patchConsultation({
        id: consultationId,
        body: { action: 'link-appointment', appointmentId },
      })
      toast.success(appointmentId ? 'Appointment updated' : 'Appointment unlinked')
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update appointment')
    }
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Change linked appointment</DialogTitle>
        <DialogDescription>
          Choose which appointment this consultation is linked to, or unlink it entirely.
        </DialogDescription>
      </DialogHeader>

      <Select
        items={items}
        value={selected}
        onValueChange={(value) => setSelected(value ?? NO_APPOINTMENT)}
      >
        <SelectTrigger className="w-full" aria-label="Linked appointment">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="start" className="w-(--anchor-width)">
          <SelectItem value={NO_APPOINTMENT}>No appointment</SelectItem>
          {sorted.map((appt) => (
            <SelectItem key={appt.id} value={appt.id} disabled={isDisabled(appt)}>
              {appointmentLabel(appt)}
              {isTakenByOther(appt) && ' (linked)'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!isLoading && sorted.length === 0 && (
        <p className="text-muted-foreground">This patient has no appointments.</p>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isPending}>
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
